<script setup lang="ts">
import {computed, onMounted, ref} from 'vue'

import type {BassLayoutSettings} from '@/generated'

const props = defineProps<{ layout: BassLayoutSettings }>()
const emit = defineEmits<{ close: [] }>()
const dialog = ref<HTMLDialogElement | null>(null)

// Dieselbe Reihenfolge und derselbe Versatz wie in der Knopfvorschau des Editors.
const displayRows = computed(() =>
    (props.layout.rows ?? []).map((row, index) => ({row, index})).reverse(),
)
const columns = computed(() => Math.min(9, Math.max(1,
    ...(props.layout.rows ?? []).map((row) => row.buttons?.length ?? 0),
)))

onMounted(() => dialog.value?.showModal())

function print() {
  window.print()
}
</script>

<template>
  <Teleport to="body">
    <dialog
        ref="dialog"
        class="bass-layout-print-dialog"
        aria-labelledby="bass-layout-print-title"
        @close="emit('close')"
    >
      <div class="bass-layout-print-toolbar">
        <p>Druckvorschau · A4 quer</p>
        <div>
          <button type="button" class="btn btn-primary" @click="print">Drucken / PDF speichern</button>
          <button type="button" class="btn btn-ghost" autofocus @click="dialog?.close()">Schließen</button>
        </div>
      </div>

      <article class="bass-layout-print-sheet" :style="{'--bass-columns': columns}">
        <header class="bass-layout-print-heading">
          <p class="bass-layout-print-eyebrow">Steirische Harmonika · Bass-Belegung</p>
          <h1 id="bass-layout-print-title">{{ layout.name || 'Meine Bass-Belegung' }}</h1>
          <p>Alle Knöpfe mit ihrer Belegung auf Druck und Zug</p>
        </header>

        <section
            v-for="{row, index} in displayRows"
            :key="index"
            class="bass-layout-print-row"
            :style="{marginLeft: `${(index + 1) * 30}px`}"
            :aria-label="`Bassreihe ${row.name}`"
        >
          <h2>{{ row.name || `Reihe ${index + 1}` }}</h2>
          <ol class="bass-layout-print-buttons">
            <li v-for="(button, buttonIndex) in row.buttons ?? []" :key="buttonIndex" class="bass-layout-print-button">
              <!--              <span class="bass-layout-print-position">{{ buttonIndex + 1 }}</span>-->
              <strong class="bass-layout-print-name">{{ button.griffschrift || '–' }}</strong>
              <!--              <span class="bass-layout-print-kind">{{ button.kind === 'chord' ? 'Akkord' : 'Grundbass' }}</span>-->
              <dl class="bass-layout-print-sounds">
                <div class="inline-flex items-center">
                  <span class="text-[7px]">Druck</span>
                  <span class="font-bold text-lg">{{ button.kind === 'chord' ? button.druck?.trim().toLowerCase() || '–' : button.druck?.trim() || '–' }}</span>
                </div>
                <div class="inline-flex items-center">
                  <span class="text-[7px]">Zug</span>
                  <span class="font-bold text-lg">{{ button.kind === 'chord' ? button.zug?.trim().toLowerCase() || '–' : button.zug?.trim() || '–' }}</span>
                </div>
              </dl>
            </li>
          </ol>
          <p v-if="!row.buttons?.length" class="bass-layout-print-empty">Keine Knöpfe konfiguriert.</p>
        </section>

        <p v-if="!layout.rows?.length" class="bass-layout-print-empty">Keine Reihen konfiguriert.</p>

        <footer class="bass-layout-print-legend">
          <p><strong>Leseschlüssel:</strong> Oben steht die Griffschrift, darunter der Knopftyp und die Töne bzw. Akkorde für beide Balgrichtungen.</p>
          <p><strong>–</strong> = keine Belegung · Die kleine Zahl bezeichnet die Position in der Reihe.</p>
          <p>Tonnamen wie in der Konfiguration: <strong>B-</strong> = deutsches b, <strong>B</strong> = deutsches h. Akkorde z. B. C, G7 oder Dm.</p>
        </footer>
      </article>
    </dialog>
  </Teleport>
</template>

<style>
.bass-layout-print-dialog {
  width: min(1120px, calc(100vw - 32px));
  max-width: none;
  max-height: calc(100dvh - 32px);
  margin: auto;
  padding: 0;
  overflow: auto;
  border: 1px solid #cbd5e1;
  border-radius: 16px;
  color: #1c1917;
  background: #fff;
  font-family: Arial, sans-serif;
  box-shadow: 0 24px 80px #0004;
}

.bass-layout-print-dialog::backdrop {
  background: #0f172a99;
}

.bass-layout-print-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  font-size: 14px;
}

.bass-layout-print-toolbar > div {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.bass-layout-print-sheet {
  padding: 32px;
}

.bass-layout-print-heading {
  margin-bottom: 28px;
}

.bass-layout-print-heading h1 {
  margin: 4px 0 8px;
  font-size: 28px;
  font-weight: 700;
}

.bass-layout-print-heading p {
  font-size: 14px;
}

.bass-layout-print-eyebrow {
  font-size: 11px !important;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.bass-layout-print-row {
  margin-bottom: 24px;
  break-inside: avoid;
}

.bass-layout-print-row h2 {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 700;
}

.bass-layout-print-buttons {
  display: grid;
  grid-template-columns: repeat(var(--bass-columns), minmax(0, 1fr));
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.bass-layout-print-button {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  aspect-ratio: 1;
  padding: 0px 15px;
  border: 1.5px solid #57534e;
  border-radius: 50%;
  break-inside: avoid;
}

.bass-layout-print-position {
  position: absolute;
  top: 6px;
  right: 18px;
  font-size: 9px;
  color: #57534e;
}

.bass-layout-print-name {
  font-size: 22px;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.bass-layout-print-kind {
  margin-bottom: 6px;
  font-size: 9px;
}

.bass-layout-print-sounds {
  color: #2b2e24;
  /* width: 80%;
  font-size: 11px;
  line-height: 1.4; */
}

.bass-layout-print-sounds > div {
  display: flex;
  justify-content: space-between;
  gap: 4px;
}

.bass-layout-print-sounds dt {
  color: #57534e;
}

.bass-layout-print-sounds dd {
  margin: 0;
  font-weight: 700;
  text-align: right;
  overflow-wrap: anywhere;
}

.bass-layout-print-empty {
  font-size: 13px;
}

.bass-layout-print-legend {
  margin-top: 28px;
  padding-top: 14px;
  border-top: 1px solid #a8a29e;
  font-size: 11px;
  line-height: 1.6;
}

@media screen and (max-width: 800px) {
  .bass-layout-print-sheet {
    padding: 20px;
  }

  .bass-layout-print-row {
    margin-left: 0 !important;
  }

  .bass-layout-print-buttons {
    grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
  }
}

/* Teleport stellt das Blatt direkt unter body: keine abgeschnittenen Scrollcontainer und
   keine leeren Seiten durch lediglich unsichtbar gemachte App-Bereiche. */
@media print {
  @page {
    size: A4 landscape;
    margin: 12mm;
  }

  body:has(> .bass-layout-print-dialog[open]) {
    margin: 0;
    background: #fff;
  }

  body:has(> .bass-layout-print-dialog[open]) > :not(.bass-layout-print-dialog) {
    display: none !important;
  }

  .bass-layout-print-dialog[open] {
    position: static;
    display: block;
    width: 100%;
    max-height: none;
    margin: 0;
    overflow: visible;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  .bass-layout-print-dialog::backdrop {
    display: none;
  }

  .bass-layout-print-toolbar {
    display: none;
  }

  .bass-layout-print-sheet {
    padding: 0;
  }
}
</style>
